import type React from "react";
import type { TFunction } from "i18next";
import type { LucideIcon } from "lucide-react";
import {
	AlertTriangle,
	BadgePercent,
	Building2,
	ChefHat,
	Clock,
	CreditCard,
	Crown,
	Landmark,
	LineChart,
	Monitor,
	Package,
	PackageSearch,
	Receipt,
	RotateCcw,
	Scale,
	ShieldCheck,
	Soup,
	Tags,
	Truck,
	UserPlus,
	Users,
	Wallet,
	Warehouse,
} from "lucide-react";
import { userHasPermission } from "@/lib/user-permissions";
import type { ReportBodyProps, ReportFilterField, ReportFiltersParams } from "./types";
import { sheetWithTotals, type SheetRows } from "./lib/export";
import {
	CategoriesReport,
	PaymentMethodsReport,
	ProductSalesReport,
	ReturnsReport,
	SalesTransactionsReport,
	SalesTrendReport,
	loadPaymentMethods,
	loadProductSales,
	loadReturns,
	loadSalesTransactions,
	paymentMethodColumns,
	productSalesColumns,
	returnColumns,
	salesTransactionColumns,
} from "./reports/sales";
import {
	FoodCostReport,
	IngredientConsumptionReport,
	InventoryReport,
	LowStockReport,
	RecipeProfitabilityReport,
} from "./reports/inventory";
import {
	ExpensesReport,
	ProfitLossReport,
	PurchasesReport,
	VatReport,
	expenseColumns,
	loadExpenses,
	loadProfits,
	loadPurchases,
	loadTax,
	profitSheet,
	purchaseColumns,
	vatSheet,
} from "./reports/finance";
import { BranchesReport, EmployeesReport, RolesReport, ShiftsReport, TerminalsReport } from "./reports/team";
import { NewCustomersReport, NewSuppliersReport, TopCustomersReport } from "./reports/contacts";

export type ReportGroupId = "sales" | "inventory" | "finance" | "team" | "contacts";

/** Group order on the reports home. */
export const REPORT_GROUPS: ReportGroupId[] = ["sales", "finance", "inventory", "team", "contacts"];

const ALL_SALE_FILTERS: ReportFilterField[] = ["period", "branch", "pos", "employee", "category", "paymentMethod"];

export interface ReportDefinition {
	/** URL segment: /reports/:id. */
	id: string;
	/** i18n key under reports.catalog.<key>.{title,description}. */
	key: string;
	group: ReportGroupId;
	icon: LucideIcon;
	/** Filters the endpoint honors; only these are shown, and only these reach the API. */
	filters: ReportFilterField[];
	component: React.ComponentType<ReportBodyProps>;
	/** Needed on top of reports.read (e.g. shifts come from the POS module). */
	permission?: string | string[];
	/** New on the reports page; marked so people discover it. */
	isNew?: boolean;
	related?: string[];
	/** Older names that still resolve here. */
	aliases?: string[];
	/** Sheet for the full workbook export, built from the same loader and columns as the report itself. */
	sheet?: (params: ReportFiltersParams, t: TFunction, currency: string) => Promise<SheetRows>;
}

const total = (rows: any[], pick: (row: any) => unknown) => rows.reduce((s, r) => s + (Number(pick(r)) || 0), 0);

export const REPORTS: ReportDefinition[] = [
	// Sales
	{
		id: "sales-trend",
		key: "salesTrend",
		group: "sales",
		icon: LineChart,
		filters: ALL_SALE_FILTERS,
		component: SalesTrendReport,
		isNew: true,
		related: ["sales-invoices", "payment-methods", "profit-loss"],
	},
	{
		id: "sales-invoices",
		key: "salesInvoices",
		group: "sales",
		icon: Receipt,
		filters: ALL_SALE_FILTERS,
		component: SalesTransactionsReport,
		aliases: ["sales", "sales-transactions"],
		related: ["sales-trend", "returns", "vat"],
		sheet: async (params, t, currency) =>
			sheetWithTotals(
				salesTransactionColumns(t, currency),
				(await loadSalesTransactions(params)).sales ?? [],
				t("reports.table.total"),
			),
	},
	{
		id: "product-sales",
		key: "productSales",
		group: "sales",
		icon: Package,
		filters: ALL_SALE_FILTERS,
		component: ProductSalesReport,
		aliases: ["products", "top-products"],
		related: ["categories", "inventory", "recipe-profitability"],
		sheet: async (params, t, currency) => {
			const rows = await loadProductSales(params);
			return sheetWithTotals(
				productSalesColumns(
					t,
					currency,
					total(rows, r => r.totalRevenue),
				),
				rows,
				t("reports.table.total"),
			);
		},
	},
	{
		id: "categories",
		key: "categories",
		group: "sales",
		icon: Tags,
		filters: ["period", "branch"],
		component: CategoriesReport,
		related: ["product-sales", "inventory"],
	},
	{
		id: "payment-methods",
		key: "paymentMethods",
		group: "sales",
		icon: CreditCard,
		filters: ["period", "branch", "pos", "employee", "category"],
		component: PaymentMethodsReport,
		related: ["shifts", "sales-invoices"],
		sheet: async (params, t, currency) => {
			const rows = await loadPaymentMethods(params);
			return sheetWithTotals(
				paymentMethodColumns(
					t,
					currency,
					total(rows, r => r.total),
				),
				rows,
				t("reports.table.total"),
			);
		},
	},
	{
		id: "returns",
		key: "returns",
		group: "sales",
		icon: RotateCcw,
		filters: ["period", "branch", "employee"],
		component: ReturnsReport,
		related: ["sales-invoices", "product-sales"],
		sheet: async (params, t, currency) =>
			sheetWithTotals(
				returnColumns(t, currency),
				(await loadReturns(params)).returns ?? [],
				t("reports.table.total"),
			),
	},
	// Finance
	{
		id: "profit-loss",
		key: "profitLoss",
		group: "finance",
		icon: Scale,
		filters: ["period", "branch"],
		component: ProfitLossReport,
		aliases: ["profits"],
		related: ["expenses", "branches", "sales-trend"],
		sheet: async (params, t) => profitSheet(t, (await loadProfits(params)).months ?? []),
	},
	{
		id: "vat",
		key: "vat",
		group: "finance",
		icon: Landmark,
		filters: ["period", "branch"],
		component: VatReport,
		aliases: ["tax"],
		related: ["sales-invoices", "purchases"],
		sheet: async (params, t) => vatSheet(t, await loadTax(params)),
	},
	{
		id: "expenses",
		key: "expenses",
		group: "finance",
		icon: Wallet,
		filters: ["period", "branch"],
		component: ExpensesReport,
		aliases: ["departments"],
		related: ["profit-loss", "branches"],
		sheet: async (params, t, currency) =>
			sheetWithTotals(expenseColumns(t, currency), await loadExpenses(params), t("reports.table.total")),
	},
	{
		id: "purchases",
		key: "purchases",
		group: "finance",
		icon: Truck,
		filters: ["period", "branch"],
		component: PurchasesReport,
		related: ["vat", "low-stock", "new-suppliers"],
		sheet: async (params, t, currency) =>
			sheetWithTotals(purchaseColumns(t, currency), await loadPurchases(params), t("reports.table.total")),
	},
	// Inventory & kitchen
	{
		id: "inventory",
		key: "inventory",
		group: "inventory",
		icon: Warehouse,
		filters: ["branch"],
		component: InventoryReport,
		isNew: true,
		related: ["low-stock", "categories"],
	},
	{
		id: "low-stock",
		key: "lowStock",
		group: "inventory",
		icon: AlertTriangle,
		filters: ["branch"],
		component: LowStockReport,
		isNew: true,
		related: ["inventory", "purchases"],
	},
	{
		id: "food-cost",
		key: "foodCost",
		group: "inventory",
		icon: BadgePercent,
		filters: ["period", "branch"],
		component: FoodCostReport,
		related: ["recipe-profitability", "ingredient-consumption"],
	},
	{
		id: "recipe-profitability",
		key: "recipeProfitability",
		group: "inventory",
		icon: ChefHat,
		filters: ["period", "branch"],
		component: RecipeProfitabilityReport,
		related: ["food-cost", "product-sales"],
	},
	{
		id: "ingredient-consumption",
		key: "ingredientConsumption",
		group: "inventory",
		icon: Soup,
		filters: ["period", "branch"],
		component: IngredientConsumptionReport,
		related: ["food-cost", "low-stock"],
	},
	// Team & operations
	{
		id: "shifts",
		key: "shifts",
		group: "team",
		icon: Clock,
		filters: ["period", "branch", "pos"],
		component: ShiftsReport,
		permission: "pos.read",
		isNew: true,
		related: ["terminals", "payment-methods"],
	},
	{
		id: "employees",
		key: "employees",
		group: "team",
		icon: Users,
		filters: ["period", "branch", "pos", "category", "paymentMethod"],
		component: EmployeesReport,
		related: ["shifts", "returns"],
	},
	{
		id: "branches",
		key: "branches",
		group: "team",
		icon: Building2,
		filters: ["period"],
		component: BranchesReport,
		related: ["profit-loss", "terminals"],
	},
	{
		id: "terminals",
		key: "terminals",
		group: "team",
		icon: Monitor,
		filters: ["period", "branch"],
		component: TerminalsReport,
		aliases: ["pos"],
		related: ["shifts", "branches"],
	},
	{
		id: "roles",
		key: "roles",
		group: "team",
		icon: ShieldCheck,
		filters: [],
		component: RolesReport,
	},
	// Customers & suppliers
	{
		id: "top-customers",
		key: "topCustomers",
		group: "contacts",
		icon: Crown,
		filters: ALL_SALE_FILTERS,
		component: TopCustomersReport,
		isNew: true,
		related: ["new-customers", "sales-invoices"],
	},
	{
		id: "new-customers",
		key: "newCustomers",
		group: "contacts",
		icon: UserPlus,
		filters: ["period"],
		component: NewCustomersReport,
		aliases: ["customers"],
		related: ["top-customers"],
	},
	{
		id: "new-suppliers",
		key: "newSuppliers",
		group: "contacts",
		icon: PackageSearch,
		filters: ["period"],
		component: NewSuppliersReport,
		aliases: ["suppliers"],
		related: ["purchases"],
	},
];

/** Reports in "Quick access" until the user pins or opens some. */
export const DEFAULT_QUICK_ACCESS = ["sales-trend", "profit-loss", "product-sales"];

export function findReport(idOrAlias: string | undefined): ReportDefinition | undefined {
	if (!idOrAlias) return undefined;
	return REPORTS.find(r => r.id === idOrAlias) ?? REPORTS.find(r => r.aliases?.includes(idOrAlias));
}

export function canOpenReport(user: Parameters<typeof userHasPermission>[0], report: ReportDefinition): boolean {
	return !report.permission || userHasPermission(user, report.permission);
}
